import { defineStore } from 'pinia';
import CompanyContactsAPI from 'dashboard/api/companyContacts';
import { throwErrorMessage } from 'dashboard/store/utils/api';
import camelcaseKeys from 'camelcase-keys';

export const useCompanyContactsStore = defineStore('companyContacts', {
  state: () => ({
    records: [],
    uiFlags: {
      fetchingList: false,
      creating: false,
      updating: false,
      deleting: false,
    },
  }),

  getters: {
    getContacts: state => camelcaseKeys(state.records, { deep: true }),
    getUIFlags: state => state.uiFlags,
    getPrimaryContact: state => {
      const contacts = camelcaseKeys(state.records, { deep: true });
      return contacts.find(c => c.isPrimary)?.contact || null;
    },
  },

  actions: {
    setUIFlag(flags) {
      this.uiFlags = { ...this.uiFlags, ...flags };
    },

    async get(companyId) {
      this.setUIFlag({ fetchingList: true });
      try {
        const { data } = await CompanyContactsAPI.get(companyId);
        this.records = data;
        return data;
      } catch (error) {
        throwErrorMessage(error);
        return [];
      } finally {
        this.setUIFlag({ fetchingList: false });
      }
    },

    async create(companyId, { contactId, role, isPrimary }) {
      this.setUIFlag({ creating: true });
      try {
        const { data } = await CompanyContactsAPI.create(companyId, {
          contactId,
          role,
          isPrimary,
        });
        this.records.push(data);
        return data;
      } catch (error) {
        throwErrorMessage(error);
        throw error;
      } finally {
        this.setUIFlag({ creating: false });
      }
    },

    async update(companyId, companyContactId, { role, isPrimary }) {
      this.setUIFlag({ updating: true });
      try {
        const { data } = await CompanyContactsAPI.update(
          companyId,
          companyContactId,
          {
            role,
            isPrimary,
          }
        );
        const index = this.records.findIndex(cc => cc.id === companyContactId);
        if (index !== -1) {
          this.records[index] = data;
        }
        return data;
      } catch (error) {
        throwErrorMessage(error);
        throw error;
      } finally {
        this.setUIFlag({ updating: false });
      }
    },

    async delete(companyId, companyContactId) {
      this.setUIFlag({ deleting: true });
      try {
        await CompanyContactsAPI.delete(companyId, companyContactId);
        this.records = this.records.filter(cc => cc.id !== companyContactId);
        return true;
      } catch (error) {
        throwErrorMessage(error);
        throw error;
      } finally {
        this.setUIFlag({ deleting: false });
      }
    },

    clearRecords() {
      this.records = [];
    },
  },
});
